import * as React from "react"
import { useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material"
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch"
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useAuth } from "../../components/Auth"

const BASE = process.env.REACT_APP_API_BASE_URL || ""

const getStatus = () =>
  fetch(`${BASE}/api/admin/status`, { credentials: "include" }).then(async (r) => {
    const body = await r.json().catch(() => null)
    if (!r.ok) throw new Error(body?.error || `status ${r.status}`)
    return body
  })

const createPod = () =>
  fetch(`${BASE}/api/admin/pod`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  }).then(async (r) => {
    const body = await r.json().catch(() => null)
    if (!r.ok) throw new Error(body?.message || body?.error || `status ${r.status}`)
    return body
  })

const terminatePod = () =>
  fetch(`${BASE}/api/admin/pod`, {
    method: "DELETE",
    credentials: "include",
  }).then(async (r) => {
    const body = await r.json().catch(() => null)
    if (!r.ok) throw new Error(body?.message || body?.error || `status ${r.status}`)
    return body
  })

function WorkerChip({ worker }) {
  if (!worker) return null
  if (worker.ok === false) {
    return <Chip size="small" color="warning" label="worker booting / unreachable" />
  }
  const label = worker.running
    ? "processing"
    : worker.model_warm
      ? "ready (model warm)"
      : "ready (model cold)"
  return <Chip size="small" color="success" label={label} />
}

export default function PodPanel() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [error, setError] = useState(null)

  const { data, isLoading } = useQuery("adminStatus", getStatus, {
    enabled: !!user?.is_admin,
    refetchInterval: 10000,
    retry: false,
  })
  const invalidate = () => queryClient.invalidateQueries("adminStatus")
  const launch = useMutation(createPod, {
    onError: (e) => setError(e.message),
    onSuccess: () => setError(null),
    onSettled: invalidate,
  })
  const kill = useMutation(terminatePod, {
    onError: (e) => setError(e.message),
    onSuccess: () => setError(null),
    onSettled: invalidate,
  })

  if (!user?.is_admin) {
    return <Alert severity="warning" sx={{ mt: 2 }}>Admins only.</Alert>
  }
  if (isLoading) return <CircularProgress sx={{ mt: 3 }} />

  const pod = data?.pod
  const queue = data?.queue || {}
  const busy = launch.isLoading || kill.isLoading

  return (
    <Box sx={{ maxWidth: 560, mt: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              GPU worker
            </Typography>
            {pod ? (
              <>
                <Chip size="small" color="info"
                  label={`${pod.gpu_type || "GPU"} · $${pod.cost_per_hr ?? "?"}/hr`} />
                <WorkerChip worker={pod.worker} />
              </>
            ) : (
              <Chip size="small" label="no pod" />
            )}
          </Stack>
          {pod && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {pod.pod_id} · up since {pod.created_at} UTC
            </Typography>
          )}
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            {pod ? (
              <Button
                variant="outlined"
                color="error"
                disabled={busy}
                startIcon={<PowerSettingsNewIcon />}
                onClick={() => {
                  if (window.confirm("Terminate the GPU pod?")) kill.mutate()
                }}
              >
                {kill.isLoading ? "Terminating…" : "Terminate pod"}
              </Button>
            ) : (
              <Button
                variant="contained"
                disabled={busy}
                startIcon={<RocketLaunchIcon />}
                onClick={() => launch.mutate()}
              >
                {launch.isLoading ? "Launching…" : "Launch pod"}
              </Button>
            )}
          </Stack>
          {!pod && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Uploads queue while no pod is running. A new pod takes a few
              minutes to boot; the dispatcher picks it up automatically, and
              idle pods auto-terminate after ~30 minutes.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Clip queue
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {["waiting", "processing", "done", "failed"].map((s) => (
              <Chip
                key={s}
                size="small"
                label={`${s}: ${queue[s] || 0}`}
                color={s === "failed" && queue[s] ? "error" : "default"}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  )
}
